import { Tooltip as RadixTooltip } from 'radix-ui';
import type { ReactNode } from 'react';

import { cn } from '@/utils/cn';

import { usePopoverContainer, type PopoverPortalTarget } from './popover-portal';

/**
 * A tooltip never carries information available nowhere else.
 *
 * It is hover-and-focus triggered, which means a touch user may never see it. Put
 * anything a user must know in the label, the description or an inline hint — the
 * tooltip is for the extra sentence, not the requirement.
 */

export const TooltipProvider = RadixTooltip.Provider;

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  popoverPortal?: PopoverPortalTarget;
  /** Open state for a tooltip driven by something other than hover. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  delayDuration?: number;
}

export const Tooltip = ({
  content,
  children,
  side = 'top',
  popoverPortal = 'body',
  open,
  onOpenChange,
  delayDuration = 300,
}: TooltipProps) => {
  const { container } = usePopoverContainer(popoverPortal);

  return (
    <RadixTooltip.Root open={open} onOpenChange={onOpenChange} delayDuration={delayDuration}>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal container={container}>
        <RadixTooltip.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            // Above pickers, tables and dialogs — portalled so overflow on a scroll
            // parent cannot clip it.
            'z-[60] max-w-xs rounded-md bg-fg px-2.5 py-1.5 text-caption text-fg-inverse shadow-e3',
            'animate-in fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          )}
        >
          {content}
          <RadixTooltip.Arrow className="fill-[var(--text-primary)]" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
};
